// src/components/employee/EmployeeDocumentsAPI.js
// Employee Documents Component with Database/API Integration
import React, { useState, useEffect } from 'react';
import {
    FileText, Download, Eye, Search, Filter, Calendar, Send,
    Building2, User, ChevronRight, X, Upload, AlertCircle, CheckCircle
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import documentBuilderService from '../../services/documentBuilderHybrid.service';
import documentDatabaseService from '../../services/documentDatabase.service';
import { getCategoryDisplayName } from '../../utils/categoryHelper';
import '../../utils/debugDocumentDelivery'; // Load debug utility

const EmployeeDocumentsAPI = () => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState('company'); // 'company' or 'personal'
    const [companyDocuments, setCompanyDocuments] = useState([]);
    const [personalDocuments, setPersonalDocuments] = useState([]);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);

    // Get current user
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    // Extract category from template name
    const extractCategoryFromTemplate = (templateName) => {
        if (!templateName) return null;

        const categoryMap = {
            'appointment': 'appointment',
            'payslip': 'payslip',
            'termination': 'termination',
            'promotion': 'promotion',
            'experience': 'experience',
            'confirmation': 'confirmation',
            'increment': 'increment',
            'transfer': 'transfer',
            'resignation': 'resignation',
            'offer': 'offer',
            'warning': 'warning',
            'appreciation': 'appreciation',
            'relieving': 'relieving',
            'internship': 'internship',
            'contract': 'contract',
            'noc': 'noc',
            'bonafide': 'bonafide',
            'salary': 'salary',
            'company': 'company'
        };

        const lowerTemplate = templateName.toLowerCase();
        for (const [key, value] of Object.entries(categoryMap)) {
            if (lowerTemplate.includes(key)) {
                return value;
            }
        }
        return null;
    };

    // Get smart document display name
    const getDocumentDisplayName = (doc) => {
        // Try category or document_type first (category is stored in document_type for some documents)
        const categoryValue = doc.category || doc.document_type || doc.templateName;
        if (categoryValue) {
            const displayName = getCategoryDisplayName(categoryValue);
            if (displayName !== 'Document') {
                return displayName;
            }
        }

        // If no category, try to extract from template_name or document_name
        const extractedCategory = extractCategoryFromTemplate(doc.templateName || doc.name);
        if (extractedCategory) {
            return getCategoryDisplayName(extractedCategory);
        }

        // If extraction failed, try to clean up template format words from name
        if (doc.name) {
            let cleanName = doc.name
                .replace(/Modern\s*&\s*Concise\s*/gi, '')
                .replace(/Modern\s*and\s*Concise\s*/gi, '')
                .replace(/Standard\s*/gi, '')
                .replace(/Professional\s*/gi, '')
                .replace(/Formal\s*/gi, '')
                .replace(/Basic\s*/gi, '')
                .replace(/Simple\s*/gi, '')
                .replace(/Classic\s*/gi, '')
                .trim();

            return cleanName || doc.name;
        }

        return doc.templateName || 'Document';
    };

    // Load documents on mount
    useEffect(() => {
        fetchDocuments();
    }, []);

    // Fetch documents from Firebase Storage
    const fetchDocuments = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch my documents (received, uploaded, company) from database
            const myDocs = await documentDatabaseService.getMyDocuments();

            // Separate documents by type
            setCompanyDocuments(myDocs.company || []);
            setPersonalDocuments(myDocs.received || []);
            setUploadedDocuments(myDocs.uploaded || []);

            console.log('Loaded documents from API:', {
                company: myDocs.company?.length || 0,
                received: myDocs.received?.length || 0,
                uploaded: myDocs.uploaded?.length || 0,
                currentUser: {
                    id: currentUser.id,
                    email: currentUser.email,
                    name: currentUser.name
                },
                companyDocs: myDocs.company,
                receivedDocs: myDocs.received
            });

            // Debug: Check localStorage directly
            console.log('Debug - Checking localStorage for documents:');
            console.log(`employeeDocuments_${currentUser.id}:`, JSON.parse(localStorage.getItem(`employeeDocuments_${currentUser.id}`) || '[]'));
            console.log(`employeeCompanyDocuments_${currentUser.id}:`, JSON.parse(localStorage.getItem(`employeeCompanyDocuments_${currentUser.id}`) || '[]'));
            console.log('allCompanyDocuments:', JSON.parse(localStorage.getItem('allCompanyDocuments') || '[]'));

            // Also fetch company-wide documents
            const companyWideDocs = await documentDatabaseService.getCompanyDocuments();
            if (companyWideDocs && companyWideDocs.length > 0) {
                // Merge with existing company documents, avoiding duplicates
                const mergedDocs = [...myDocs.company || []];
                companyWideDocs.forEach(doc => {
                    if (!mergedDocs.find(d => d.id === doc.id)) {
                        mergedDocs.push(doc);
                    }
                });
                setCompanyDocuments(mergedDocs);
            }

            // Additional check: Directly look for documents sent to this employee
            const directEmployeeDocs = JSON.parse(localStorage.getItem(`employeeCompanyDocuments_${currentUser.id}`) || '[]');
            if (directEmployeeDocs.length > 0) {
                console.log(`Found ${directEmployeeDocs.length} documents directly in employeeCompanyDocuments_${currentUser.id}`);
                // Add to company documents if not already there
                setCompanyDocuments(prev => {
                    const existing = prev || [];
                    directEmployeeDocs.forEach(doc => {
                        if (!existing.find(d => d.id === doc.id)) {
                            existing.push(doc);
                        }
                    });
                    return existing;
                });
            }

            // Check allCompanyDocuments for documents sent to this user
            const allCompanyDocs = JSON.parse(localStorage.getItem('allCompanyDocuments') || '[]');
            const docsForThisUser = allCompanyDocs.filter(doc => doc.sentTo === currentUser.id);
            if (docsForThisUser.length > 0) {
                console.log(`Found ${docsForThisUser.length} documents in allCompanyDocuments for this user`);
                // Add to company documents if not already there
                setCompanyDocuments(prev => {
                    const existing = prev || [];
                    docsForThisUser.forEach(doc => {
                        if (!existing.find(d => d.id === doc.id)) {
                            existing.push(doc);
                        }
                    });
                    return existing;
                });
            }

            // TEMPORARY FIX: Check for documents sent to this user by email (ID mismatch fix)
            const docsForThisUserByEmail = allCompanyDocs.filter(doc =>
                doc.sentToEmail === currentUser.email && doc.sentTo !== currentUser.id
            );
            if (docsForThisUserByEmail.length > 0) {
                console.log(`⚠️ FOUND ${docsForThisUserByEmail.length} documents sent by EMAIL (ID mismatch!)`);
                docsForThisUserByEmail.forEach(doc => {
                    console.log(`Document: ${doc.templateName} was sent to ID: ${doc.sentTo} but user's ID is: ${currentUser.id}`);
                });
                // Add these documents to company documents
                setCompanyDocuments(prev => {
                    const existing = prev || [];
                    docsForThisUserByEmail.forEach(doc => {
                        if (!existing.find(d => d.id === doc.id)) {
                            existing.push(doc);
                        }
                    });
                    return existing;
                });
            }

        } catch (error) {
            console.error('Error fetching documents:', error);

            // Don't show error if it's just "no documents found" (e.g., 404 or empty response)
            // Only show error for actual network/server failures
            if (error.response && (error.response.status === 404 || error.response.status === 204)) {
                // No documents found - this is normal, just show empty state
                console.log('No documents found for user (404/204) - showing empty state');
                setCompanyDocuments([]);
                setPersonalDocuments([]);
                setUploadedDocuments([]);
            } else if (error.message && error.message.includes('Not Found')) {
                // Another form of "no documents" - show empty state
                console.log('No documents found - showing empty state');
                setCompanyDocuments([]);
                setPersonalDocuments([]);
                setUploadedDocuments([]);
            } else {
                // Actual error - show error message
                setError('Failed to load documents. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle file upload
    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setLoading(true);
            setError('');

            // Upload to Firebase
            const result = await documentBuilderService.uploadToFirebase(file, {
                uploadedBy: currentUser.name || currentUser.email,
                employeeId: currentUser.id,
                uploadedAt: new Date().toISOString(),
                type: 'personal'
            });

            // Save document metadata
            const documentData = {
                id: result.documentId,
                name: file.name,
                url: result.url,
                firebasePath: result.path,
                uploadedAt: new Date().toISOString(),
                uploadedBy: currentUser.name || currentUser.email,
                type: 'personal',
                size: file.size,
                mimeType: file.type
            };

            // Update local state
            setUploadedDocuments(prev => [...prev, documentData]);
            setPersonalDocuments(prev => [...prev, documentData]);

            setUploadedFile(documentData);
            setSuccess('Document uploaded successfully!');
            setTimeout(() => setSuccess(''), 3000);

            // Refresh documents
            await fetchDocuments();
        } catch (error) {
            console.error('Error uploading file:', error);
            setError('Failed to upload document. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // View/Download document - prioritize PDF URL
    const handleView = (document) => {
        try {
            const pdfUrl = document.url || document.pdfUrl || document.firebaseUrl || document.document_url;

            if (pdfUrl && pdfUrl.trim() !== '') {
                console.log('📄 Opening PDF:', pdfUrl);
                window.open(pdfUrl, '_blank', 'noopener,noreferrer');
            } else {
                console.log('📋 No PDF URL, showing content modal');
                setSelectedDocument(document);
            }
        } catch (error) {
            console.error('Error viewing document:', error);
            setError('Failed to view document');
        }
    };

    // Download document
    const handleDownload = async (document) => {
        try {
            const pdfUrl = document.url || document.pdfUrl || document.firebaseUrl || document.document_url;

            if (pdfUrl) {
                // Direct download from URL
                window.open(pdfUrl, '_blank');
            } else if (document.id) {
                // Get download URL from API
                const url = await documentBuilderService.getDocumentUrl(document.id);
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error('Error downloading document:', error);
            setError('Failed to download document');
        }
    };

    // Search documents
    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            fetchDocuments();
            return;
        }

        try {
            setLoading(true);
            const results = await documentBuilderService.searchDocuments(searchQuery, {
                employeeId: currentUser.id,
                type: activeTab === 'company' ? 'company' : 'personal'
            });

            if (activeTab === 'company') {
                setCompanyDocuments(results);
            } else {
                setPersonalDocuments(results);
            }
        } catch (error) {
            console.error('Error searching documents:', error);
            setError('Search failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Filter documents based on search query (local filter)
    const filterDocuments = (documents) => {
        if (!searchQuery) return documents;

        return documents.filter(doc => {
            const searchLower = searchQuery.toLowerCase();
            return (
                doc.templateName?.toLowerCase().includes(searchLower) ||
                doc.category?.toLowerCase().includes(searchLower) ||
                doc.sentBy?.toLowerCase().includes(searchLower) ||
                doc.name?.toLowerCase().includes(searchLower) ||
                doc.content?.toLowerCase().includes(searchLower)
            );
        });
    };

    // Get filtered documents
    const filteredCompanyDocs = filterDocuments(companyDocuments);
    const filteredPersonalDocs = filterDocuments(personalDocuments);

    // Debug: Log current state
    console.log('Current document state:', {
        companyDocumentsCount: companyDocuments.length,
        filteredCompanyDocsCount: filteredCompanyDocs.length,
        activeTab,
        searchQuery
    });

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Document preview modal
    const renderDocumentPreview = () => {
        if (!selectedDocument) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="max-w-4xl w-full max-h-[80vh] overflow-auto rounded-lg p-6 bg-white">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold">
                            {getDocumentDisplayName(selectedDocument)}
                        </h3>
                        <button
                            onClick={() => setSelectedDocument(null)}
                            className="p-2 hover:bg-gray-100 rounded"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Document metadata */}
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="font-medium">Category:</span>{' '}
                                {selectedDocument.category || 'N/A'}
                            </div>
                            <div>
                                <span className="font-medium">Date:</span>{' '}
                                {formatDate(selectedDocument.receivedAt || selectedDocument.generatedAt)}
                            </div>
                            <div>
                                <span className="font-medium">Sent By:</span>{' '}
                                {selectedDocument.sentBy || 'HR Department'}
                            </div>
                            <div>
                                <span className="font-medium">Status:</span>{' '}
                                <span className={`px-2 py-1 rounded text-xs ${
                                    selectedDocument.status === 'received'
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                    {selectedDocument.status || 'Received'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Document content or download link */}
                    {selectedDocument.content ? (
                        <div
                            className="prose dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{ __html: selectedDocument.content }}
                        />
                    ) : (
                        <div className="text-center py-8">
                            {selectedDocument.url || selectedDocument.pdfUrl ? (
                                <div>
                                    <p className="mb-4 text-gray-600">
                                        This document is available as a file.
                                    </p>
                                    <button
                                        onClick={() => handleDownload(selectedDocument)}
                                        className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600
                                            flex items-center gap-2 mx-auto"
                                    >
                                        <Download className="w-5 h-5" />
                                        Download Document
                                    </button>
                                </div>
                            ) : (
                                <p className="text-gray-500">Document content not available for preview.</p>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="mt-6 flex gap-3 justify-end">
                        {(selectedDocument.url || selectedDocument.pdfUrl) && (
                            <button
                                onClick={() => handleDownload(selectedDocument)}
                                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600
                                    flex items-center gap-2"
                            >
                                <Download className="w-4 h-4" />
                                Download
                            </button>
                        )}
                        <button
                            onClick={() => setSelectedDocument(null)}
                            className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Render document card
    const renderDocumentCard = (doc) => (
        <div key={doc.id} className={`
            p-4 rounded-lg border transition-all hover:shadow-lg cursor-pointer
            ${theme === 'dark'
                ? 'bg-gray-800 border-gray-700 hover:border-blue-500'
                : 'bg-white border-gray-200 hover:border-blue-400'
            }
        `}>
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${
                        theme === 'dark' ? 'bg-gray-700' : 'bg-blue-50'
                    }`}>
                        <FileText className="w-5 h-5 text-blue-500" />
                    </div>
                    <div>
                        <h4 className="font-semibold">
                            {getDocumentDisplayName(doc)}
                        </h4>
                    </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs ${
                    doc.status === 'received' || doc.type === 'company'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                }`}>
                    {doc.status === 'received' ? 'Received' : doc.type === 'company' ? 'Company' : 'Personal'}
                </span>
            </div>

            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(doc.receivedAt || doc.generatedAt || doc.uploadedAt)}</span>
                </div>
                {doc.sentBy && (
                    <div className="flex items-center gap-2">
                        <Send className="w-4 h-4" />
                        <span>{doc.sentBy}</span>
                    </div>
                )}
            </div>

            <div className="mt-4 flex gap-2">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleView(doc);
                    }}
                    className="px-3 py-1.5 bg-blue-500 text-white rounded hover:bg-blue-600
                        flex items-center gap-1 text-sm"
                >
                    <Eye className="w-4 h-4" />
                    View
                </button>
                {(doc.url || doc.pdfUrl) && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(doc);
                        }}
                        className="px-3 py-1.5 bg-green-500 text-white rounded hover:bg-green-600
                            flex items-center gap-1 text-sm"
                    >
                        <Download className="w-4 h-4" />
                        Download
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
                        <FileText className="w-8 h-8 text-blue-500" />
                        HR Documents
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400">
                        Access and manage your employment documents
                    </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        {error}
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        {success}
                    </div>
                )}

                {/* Search and Actions Bar */}
                <div className="mb-6 flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <input
                            type="text"
                            placeholder="Search documents..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            className={`w-full px-4 py-2 pl-10 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border-gray-700 text-white'
                                    : 'bg-white border-gray-300'
                            }`}
                        />
                        <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                    </div>
                    <button
                        onClick={handleSearch}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600
                            flex items-center gap-2"
                    >
                        <Filter className="w-5 h-5" />
                        Search
                    </button>
                    <button
                        onClick={fetchDocuments}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600
                            flex items-center gap-2"
                    >
                        Refresh
                    </button>
                </div>

                {/* Tabs */}
                <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex gap-4">
                        <button
                            onClick={() => setActiveTab('company')}
                            className={`pb-3 px-4 font-medium transition-colors border-b-2 ${
                                activeTab === 'company'
                                    ? 'border-blue-500 text-blue-500'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <Building2 className="w-5 h-5" />
                                Company Documents
                                {filteredCompanyDocs.length > 0 && (
                                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs rounded-full">
                                        {filteredCompanyDocs.length}
                                    </span>
                                )}
                            </div>
                        </button>
                        <button
                            onClick={() => setActiveTab('personal')}
                            className={`pb-3 px-4 font-medium transition-colors border-b-2 ${
                                activeTab === 'personal'
                                    ? 'border-blue-500 text-blue-500'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5" />
                                My Documents
                                {filteredPersonalDocs.length > 0 && (
                                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs rounded-full">
                                        {filteredPersonalDocs.length}
                                    </span>
                                )}
                            </div>
                        </button>
                    </div>
                </div>

                {/* Content */}
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    </div>
                ) : (
                    <div>
                        {/* Upload Section (for personal documents) */}
                        {activeTab === 'personal' && (
                            <div className={`mb-6 p-6 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border-gray-700'
                                    : 'bg-white border-gray-200'
                            }`}>
                                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <Upload className="w-5 h-5" />
                                    Upload Personal Document
                                </h3>
                                <input
                                    type="file"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    onChange={handleFileUpload}
                                    className="block w-full text-sm text-gray-900 dark:text-gray-100
                                        file:mr-4 file:py-2 file:px-4
                                        file:rounded-lg file:border-0
                                        file:text-sm file:font-semibold
                                        file:bg-blue-50 file:text-blue-700
                                        hover:file:bg-blue-100
                                        dark:file:bg-blue-900/30 dark:file:text-blue-400"
                                    disabled={loading}
                                />
                                <p className="mt-2 text-sm text-gray-500">
                                    Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                                </p>
                            </div>
                        )}

                        {/* Documents Grid */}
                        {activeTab === 'company' ? (
                            <div>
                                {filteredCompanyDocs.length === 0 ? (
                                    <div className="text-center py-12">
                                        <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                        <p className="text-gray-500">No company documents found</p>
                                        <p className="text-sm text-gray-400 mt-2">
                                            Company documents sent by HR will appear here
                                        </p>
                                    </div>
                                ) : (
                                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {filteredCompanyDocs.map(renderDocumentCard)}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                {filteredPersonalDocs.length === 0 && uploadedDocuments.length === 0 ? (
                                    <div className="text-center py-12">
                                        <User className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                                        <p className="text-gray-500">No personal documents found</p>
                                        <p className="text-sm text-gray-400 mt-2">
                                            Upload your personal documents or received documents will appear here
                                        </p>
                                    </div>
                                ) : (
                                    <div>
                                        {/* Received Documents */}
                                        {filteredPersonalDocs.length > 0 && (
                                            <div className="mb-8">
                                                <h3 className="text-lg font-semibold mb-4">Received Documents</h3>
                                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    {filteredPersonalDocs.map(renderDocumentCard)}
                                                </div>
                                            </div>
                                        )}

                                        {/* Uploaded Documents */}
                                        {uploadedDocuments.length > 0 && (
                                            <div>
                                                <h3 className="text-lg font-semibold mb-4">Uploaded Documents</h3>
                                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                    {uploadedDocuments.map(renderDocumentCard)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Document Statistics */}
                <div className="mt-8 p-6 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <h3 className="text-lg font-semibold mb-4">Document Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                            <p className="text-2xl font-bold text-blue-500">{companyDocuments.length}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Company Documents</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-green-500">{personalDocuments.length}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Received Documents</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-purple-500">{uploadedDocuments.length}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Uploaded Documents</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-orange-500">
                                {companyDocuments.length + personalDocuments.length + uploadedDocuments.length}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">Total Documents</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Document Preview Modal */}
            {renderDocumentPreview()}
        </div>
    );
};

export default EmployeeDocumentsAPI;